import React, { useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowLeft } from "lucide-react";

function ProductPage() {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // المنتج الافتراضي إذا ما إجا من الـ Link
  const product = location.state || {
    id: productId,
    name: "Sample Product",
    image: "https://via.placeholder.com/800x500",
    type: "menu", // "menu" | "room" | "booking" | "rental"
    description: "This is a detailed description of the product.",
    price: 100,
    companyId: "company-123",
    optionalField: { label: "Number of Persons", type: "number" },
    availableTimes: ["10:00", "10:15", "10:30"],
  };

  // 🟢 States عامة
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState(product.availableTimes?.[0] || "");
  const [optionalValue, setOptionalValue] = useState(1);
  const [rentalDuration, setRentalDuration] = useState("Hourly");
  const [showPopup, setShowPopup] = useState(false);

  const { addToCart } = useCart();

  const handleAddToCart = () => {
    const cartItem = {
      productId: product.id,
      name: product.name,
      type: product.type,
      companyId: product.companyId,
      price: product.price,
      quantity,
      notes,
      image:product.image,
      ...(product.type === "booking"
        ? {
            date,
            time,
            ...(product.optionalField
              ? { [product.optionalField.label]: optionalValue }
              : {}),
          }
        : {}),
      ...(product.type === "rental"
        ? {
            date,
            time,
            rentalDuration,
            ...(product.optionalField
              ? { [product.optionalField.label]: optionalValue }
              : {}),
          }
        : {}),
    };

    addToCart(cartItem);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2000); // hide after 2s
  };

  return (
    <div className="container mx-auto py-10 px-4 relative">
    <button
        onClick={() => navigate(-1)}
        className="fixed bottom-6 left-6 flex items-center gap-2 px-4 py-2 bg-white shadow-lg rounded-xl hover:bg-gray-100 transition z-50"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>
      <div className="bg-white shadow-lg rounded-2xl overflow-hidden">
        {/* صورة المنتج */}
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-72 object-cover"
        />

        {/* التفاصيل */}
        <div className="p-6">
          <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
          <p className="mt-3 text-gray-600">{product.description}</p>
          {product.price && (
            <p className="mt-3 text-lg font-semibold text-indigo-600">
              Price: {product.price} $
            </p>
          )}

          {/* 🟢 الكمية */}
          {(product.type === "menu" || product.type === "room") && (
            <div className="mt-5">
              <label className="block mb-2 font-medium text-gray-700">
                Quantity:
              </label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="border p-2 rounded-lg w-28 shadow-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}

          {/* 🟢 الملاحظات */}
          <div className="mt-5">
            <label className="block mb-2 font-medium text-gray-700">
              Notes (optional):
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="border p-3 rounded-lg w-full shadow-sm focus:ring-2 focus:ring-indigo-500"
              rows={3}
              placeholder="Write any special requests..."
            />
          </div>

          {/* 🟢 الحجز */}
          {product.type === "booking" && (
            <div className="mt-6 space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Date:
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Select Time:
                </label>
                <select
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                >
                  {product.availableTimes?.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              {product.optionalField && (
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    {product.optionalField.label}:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={optionalValue}
                    onChange={(e) => setOptionalValue(Number(e.target.value))}
                    className="border p-2 rounded-lg w-28 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}
            </div>
          )}

          {/* 🟢 الاستئجار */}
          {product.type === "rental" && (
            <div className="mt-6 space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Date:
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Select Time:
                </label>
                <select
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                >
                  {product.availableTimes?.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Rental Duration:
                </label>
                <select
                  value={rentalDuration}
                  onChange={(e) => setRentalDuration(e.target.value)}
                  className="border p-2 rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Hourly">Hourly</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>

              {product.optionalField && (
                <div>
                  <label className="block mb-2 font-medium text-gray-700">
                    {product.optionalField.label}:
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={optionalValue}
                    onChange={(e) => setOptionalValue(Number(e.target.value))}
                    className="border p-2 rounded-lg w-28 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}
            </div>
          )}

          {/* الأزرار */}
          <div className="mt-8 flex gap-4">
            <button
              onClick={handleAddToCart}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl shadow-md hover:bg-indigo-700 transition"
            >
              Add to Cart
            </button>
{/** to={`/company/${product.companyId}`} */}
            <Link
              to={`/companyexample`}
              className="px-6 py-3 bg-gray-100 text-gray-800 rounded-xl shadow hover:bg-gray-200 transition"
            >
              View Company Page
            </Link>
          </div>
        </div>
      </div>

      {/* ✅ Popup لما يضيف للسلة */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 bg-white border shadow-lg rounded-xl px-5 py-3 flex items-center gap-3"
          >
            <CheckCircle className="text-green-500 w-6 h-6" />
            <span className="font-medium text-gray-800">
              Added to cart successfully
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ProductPage;